import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../../components/Layout/MainLayout';
import { Plus, Edit, Trash2, Search, X, Image as ImageIcon } from 'lucide-react';
import './Categories.css';
import { handleGetApi, handlePostApi } from '../../api/api';
import Cookies from 'js-cookie';

const Categories = () => {
  const token = JSON.parse(Cookies.get('auth_token'));
  const headers = {
    Authorization: token
  }
  const [categories, setCategories] = useState([]);
  const fetchCategory = async () => {
    const res = await handleGetApi('admin/get/category', headers)
    setCategories(res.data)
  }
  useEffect(() => {
    fetchCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    image: '',
  });

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        image: category.image || '',
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        slug: '',
        image: '',
      });
    }
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sendData = new FormData();
    sendData.append('name', formData.name);
    sendData.append('slug', formData.slug);
    
    if (formData.image instanceof File) {
      sendData.append('category', formData.image);
    }

    if (editingCategory) {
      sendData.append('id', editingCategory.id);
      const res = await handlePostApi(`admin/update/category`, sendData, headers);
      const newImage = res?.image ? res?.image : formData.image;
      setCategories(categories?.map(c => c.id === editingCategory.id ? { 
        ...formData, 
        id: c.id, 
        image: newImage, 
        productCount: c.productCount 
      } : c));
    } else {
      const res = await handlePostApi(`admin/add/category`, sendData, headers);
      const newImage = res?.image;
      setCategories([...categories, { 
        ...formData, 
        id: res?.insertId, 
        image: newImage, 
        productCount: 0 
      }]);
    }
    handleCloseModal();
  };
  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    return categories.filter(item => item?.name.toLowerCase().includes(search.toLocaleLowerCase()))
  }, [categories, search])
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      const res = await handlePostApi('admin/delete/category', { id }, headers);
      setCategories(categories?.filter(c => c.id !== id));
    }
  };


  return (
    <MainLayout title="Category Management">
      <div className="categories-container fade-in">
        <div className="categories-header">
          <div className="search-filters">
            <div className="search-input">
              <Search size={18} />
              <input type="text" value={search} onChange={(e) => { setSearch(e.target.value) }} placeholder="Search categories..." />
            </div>
          </div>
          <button className="add-btn" onClick={() => handleOpenModal()}>
            <Plus size={20} />
            <span>Add Category</span>
          </button>
        </div>

        <div className="categories-card">
          <div className="table-responsive">
            <table className="categories-table">
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>Category Slug</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories && filteredCategories?.map((category) => (
                  <tr key={category.id}>
                    <td className="category-cell">
                      <div className="category-img-wrapper">
                        {category?.image ? (
                          <img 
                            src={
                              typeof category.image === 'string' && category.image.startsWith('blob:')
                                ? category.image
                                : `${process.env.REACT_APP_IMAGE_URL}category/${category.image}`
                            } 
                            alt={category.name} 
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <ImageIcon size={20} className="text-muted" />
                        )}
                      </div>
                      <div className="category-info">
                        <span className="category-name">{category?.name}</span>
                      </div>
                    </td>
                    <td className="category-desc">{category?.slug}</td>

                    <td>
                      <div className="action-btns">
                        <button className="edit-btn" onClick={() => handleOpenModal(category)}>
                          <Edit size={18} />
                        </button>
                        <button className="delete-btn" onClick={() => handleDelete(category.id)}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content fade-in">
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
              <button className="close-modal" onClick={handleCloseModal}><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="category-form">
              <div className="form-group">
                <label>Category Image</label>
                <div className="image-upload-wrapper">
                  {formData.image ? (
                    <div className="preview-container">
                      <img
                        src={
                          typeof formData.image === 'string'
                            ? (formData.image.startsWith('blob:') ? formData.image : `${process.env.REACT_APP_IMAGE_URL}category/${formData.image}`)
                            : URL.createObjectURL(formData.image)
                        }
                        alt="Category Preview"
                      />
                      <button
                        type="button"
                        className="remove-img"
                        onClick={() => setFormData({ ...formData, image: '' })}
                        title="Remove Image"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="category-image-input" className="upload-box">
                      <ImageIcon size={24} />
                      <span>Upload Image</span>
                      <input
                        type="file"
                        id="category-image-input"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setFormData({ ...formData, image: file });
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label> Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Chair"
                  required
                />
              </div>
              <div className="form-group">
                <label> Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="e.g. chair"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={handleCloseModal}>Cancel</button>
                <button type="submit" className="save-btn">{editingCategory ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Categories;
